/**
 * Vista previa en un teléfono: la página del consumidor DE VERDAD, embebida.
 *
 * Por qué un `<iframe>` y no una maqueta
 * --------------------------------------
 * La pantalla que ve el consumidor tiene un presupuesto de 30 KB, su propia
 * escala tipográfica y reglas que no son las del dashboard (el tono lo decide
 * `states.ts`, no la copia). Una maqueta que la imite queda desalineada en
 * cuanto una de las dos cambia, y lo hace en silencio: la marca aprueba un
 * texto mirando algo que no es lo que se publica.
 *
 * **No toca datos reales.** La URL lleva `?preview=<estado>`, que hace que la
 * página use datos de muestra y no consulte la API de verificación. Ninguna
 * unidad, ningún lote y ningún escaneo participan.
 */
import { type ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';

export interface PhonePreviewProps {
  src: string;
  title: string;
}

export function PhonePreview({ src, title }: PhonePreviewProps): ReactNode {
  const { t } = useI18n();

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="w-full max-w-[360px] overflow-hidden rounded-[2rem] border-8 border-ink/80 bg-surface shadow-lg"
        // 16:9 largo, aproximadamente un teléfono de gama media — que es el
        // aparato en el que esto se lee de verdad, no un tope de línea.
        style={{ aspectRatio: '9 / 17' }}
      >
        <iframe
          key={src}
          src={src}
          title={title}
          className="h-full w-full border-0 bg-white"
          // La vista previa sólo tiene que pintarse. Sin permisos: si algún día
          // el JSON de un tenant llegara a inyectar algo, no tiene desde dónde
          // salir. `allow-scripts` es lo único que necesita la página.
          sandbox="allow-scripts"
          loading="lazy"
        />
      </div>
      <p className="max-w-[360px] text-center text-xs text-ink-subtle">
        {t('verification.preview.note')}
      </p>
    </div>
  );
}
